import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { vendorLoginContext } from '../../Context/vendorLogin';
import css from './style.module.css';
import { changePasswordSchema } from '../../lib/Yup Validation/VendorsSchemas';
import { DomainSwitchContext } from '../../Context/DomainSwitch.Context';
import LoadingForget from '../LoadingPages/LoadingForget';
import { AppContext } from '../../Context/AppContext';
import { useOktaConfig } from '../../Context/CredentialsContext';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../../lib/firebase';


export default function ForgetPassword() {
    const { changePassword, otpCodeNumber } = useContext(vendorLoginContext);
    const { setLoginToken } = useOktaConfig();
    const { logo } = useContext(DomainSwitchContext);
    const { session } = useContext(AppContext);

    const [loading, setLoading] = useState(true);
    const [data, setData] = useState({ password: '' });
    const [spinner, setSpinner] = useState(false);
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const [otp, setOtp] = useState(['', '', '', '']); // OTP state
    const [passwordVisible, setPasswordVisible] = useState(false);


    const inputRef = useRef([]);
    useEffect(() => {
        if (logo === null)
            setLoading(true); // Show LoadingOtp if logo is null
        else
            setLoading(false);
    }, [logo]);

    if (loading) {
        return <LoadingForget />;
    }


    const handleChange = (e, index) => {
        const value = e.target.value;
        if (value === '' || /^[0-9]$/.test(value)) {
            const newOtp = [...otp];
            newOtp[index] = value;
            setOtp(newOtp);

            if (value && index < otp.length - 1) {
                inputRef.current[index + 1].focus();
            }
        }
    };

    const handleBackspace = (e, index) => {
        if (e.key === 'Backspace' && otp[index] === '' && index > 0) {
            inputRef.current[index - 1].focus();
        }
    };

    const getData = (e) => {
        const { name, value } = e.target;
        setData((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    // In ForgetPassword.jsx
    const login = async () => {
        setError('');
        setSpinner(true);
        try {
            const otpString = otp.join('');
            const validationData = { password: data.password, otp: otpString };
            await changePasswordSchema.validate(validationData, { abortEarly: false });

            if (otpCodeNumber.otpCode.toString() === otpString) {
                const { password } = data;
                const res = await changePassword(otpCodeNumber?.email, password, otpString, otpCodeNumber.expiry);

                if (res.status === 200) {
                    if (res.data.active === false) {
                        setError('Your account is inactive. Please contact support.');
                        return;
                    }

                    // 👇 Re-authenticate with new password to get a fresh token
                    const firebaseRes = await signInWithEmailAndPassword(
                        auth,
                        otpCodeNumber.email,
                        password
                    );
                    const freshToken = await firebaseRes.user.getIdToken();

                    // 👇 Update localStorage with fresh token
                    const updatedAuth = {
                        ...res.data.firebaseLogin,
                        idToken: freshToken,
                        accessToken: freshToken,
                    };
                    localStorage.setItem("Firebase Auth", JSON.stringify(updatedAuth));
                    sessionStorage.setItem('Team', res.data.userRole);
                    setLoginToken(freshToken);

                    const from = sessionStorage.getItem('from');
                    if (from) {
                        session();
                        navigate(from === "/" ? "/policies" : from);
                    } else {
                        localStorage.setItem("Vendor User", res.data.data.uid);
                        navigate('/vendors');
                    }
                } else {
                    setError('Password change failed.');
                }
            } else {
                setError('Invalid OTP');
            }
        } catch (error) {
            setError(error.errors ? error.errors.join(", ") : 'An unexpected error occurred');
        } finally {
            setSpinner(false);
        }
    };

    return (

        <div className={`rounded documents ${css.borderForm}`}>
            <header className={`text-primary py-3 fw-bold px-5 mt-2 border-bottom border-1 ${css.headerBottomLineColor} rounded-top text-center`}>
                <img src={logo} alt="Company logo" className={css.logo} />
            </header>
            <main className="py-3 text-center px-4">
                <p className="text-center fw-bold mt-2 mb-2 " style={{ fontWeight: 400, fontSize: "1.375rem" }}>
                    Verify Your Identity
                </p>
                <p className='mb-3  ms-1' style={{ width: "240px", fontWeight: 400, fontSize: "0.875rem" }}>Please check your email for the MFA code and enter it below</p>

                <label className="d-flex justify-start mb-2 ms-2">New Password</label>
                <div className=" position-relative">
                    <input
                        type={passwordVisible ? "text" : "password"}
                        id='password'
                        name='password'
                        className={`form-control ${css.placeholderPosition} ${css.inputAttributes}`}
                        onChange={getData}
                        placeholder='password'
                    />
                    <div
                        className="position-absolute top-50 end-0 translate-middle-y pe-3"
                        style={{ cursor: 'pointer' }}
                        onClick={() => setPasswordVisible(!passwordVisible)} // Toggle visibility
                    >
                        {passwordVisible ? (
                            <i className="fa-solid fa-eye-slash" />  // Font Awesome eye-slash icon
                        ) : (
                            <i className="fa-solid fa-eye" />  // Font Awesome eye icon
                        )}
                    </div>
                </div>
                <label htmlFor="otp" className="d-flex justify-start my-2 ms-2">MFA Code</label>
                <div className="d-flex justify-content-between">
                    {otp.map((value, index) => (
                        <input
                            key={index}
                            type="text"
                            id={`otp-${index}`}
                            name="otp"
                            value={value}
                            maxLength="1"
                            className={css.otpInput}
                            onChange={(e) => handleChange(e, index)}
                            onKeyDown={(e) => handleBackspace(e, index)}
                            ref={(el) => (inputRef.current[index] = el)}
                            placeholder="X"
                        />
                    ))}
                </div>
                <button
                    onClick={login}
                    className="btn text-white my-4 mx-3 px-5 py-2 rounded-1"
                    style={{ backgroundColor: "var(--accent)" }}
                    disabled={spinner}
                >
                    Create Password
                    {spinner && <i className="ms-3 fas fa-spinner fa-pulse"></i>}
                </button>
                {error && (
                    <div className="d-flex justify-content-center">
                        <div className="alert alert-danger" style={{ width: "80%" }}>
                            {error}
                        </div>
                    </div>
                )}

            </main>
        </div>

    );
}
